/** /launch_policies/ — cross-tenant Launch_Policy catalog (list + CRUD + used_by).
 *
 * A launch policy is a named bundle of feature flags that gates what a tour
 * (or a tenant, as its default) is allowed to do at launch — messaging
 * channels, webhooks, chat, etc. `tour_operator` is nullable: a null tenant is
 * a system/global policy shared across operators.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Launch_Policy has no optimistic-concurrency token, so
 * writes are last-writer-wins (no conflict banner, like regions).
 */
import { apiDelete, apiGet, apiPatch, apiPost, withChangeReason } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** The 12 boolean feature flags a launch policy carries, in display order.
 *  Grouped for the form dialog by `group`. */
export interface LaunchPolicyFlag {
  key: LaunchPolicyFlagKey;
  label: string;
  group: 'Messaging' | 'Webhooks' | 'Channels';
}

export type LaunchPolicyFlagKey =
  | 'magic_links'
  | 'chat_enabled'
  | 'incoming_webhooks'
  | 'outgoing_webhooks'
  | 'outgoing_subscriptions'
  | 'outgoing_emails'
  | 'outgoing_emails_to_address'
  | 'outgoing_sms'
  | 'outgoing_sms_to_number'
  | 'outgoing_whatsapp'
  | 'outgoing_whatsapp_to_number'
  | 'outgoing_airbnb';

export const LAUNCH_POLICY_FLAGS: LaunchPolicyFlag[] = [
  { key: 'magic_links', label: 'Magic links', group: 'Messaging' },
  { key: 'chat_enabled', label: 'Chat', group: 'Messaging' },
  { key: 'incoming_webhooks', label: 'Incoming webhooks', group: 'Webhooks' },
  { key: 'outgoing_webhooks', label: 'Outgoing webhooks', group: 'Webhooks' },
  { key: 'outgoing_subscriptions', label: 'Subscriptions', group: 'Webhooks' },
  { key: 'outgoing_emails', label: 'Emails', group: 'Channels' },
  { key: 'outgoing_emails_to_address', label: 'Emails to any address', group: 'Channels' },
  { key: 'outgoing_sms', label: 'SMS', group: 'Channels' },
  { key: 'outgoing_sms_to_number', label: 'SMS to any number', group: 'Channels' },
  { key: 'outgoing_whatsapp', label: 'WhatsApp', group: 'Channels' },
  { key: 'outgoing_whatsapp_to_number', label: 'WhatsApp to any number', group: 'Channels' },
  { key: 'outgoing_airbnb', label: 'Airbnb', group: 'Channels' },
];

/** The flag portion of a record — all 12 booleans. */
export type LaunchPolicyFlags = Record<LaunchPolicyFlagKey, boolean>;

export interface LaunchPolicyListItem extends LaunchPolicyFlags {
  id: number;
  /** Owning tenant. Null for system/global policies — `Launch_Policy.tour_operator`
   *  is nullable, so the backend serialises `tenant: null` for those rows. */
  tenant: TenantRef | null;
  name: string;
  description: string | null;
  created_ts: string | null;
  updated_ts: string | null;
  /** Referencing tours + tenants defaulting to this policy (the two PROTECTed
   *  FKs that block a delete). 0 = orphan, safe to delete. Sortable. */
  in_use_count: number;
}

export interface LaunchPolicyDetail extends LaunchPolicyListItem {
  atag: NamedRef[];
  /** Reference counts of the two PROTECTed FKs that block a delete. */
  used_by: {
    tours: number;
    default_for_tenants: number;
  };
}

/** Create/update body — a subset of flags may be sent (omitted default false on
 *  create). `tour_operator` null = system/global policy. */
export interface LaunchPolicyWrite extends Partial<LaunchPolicyFlags> {
  tour_operator?: number | null;
  name?: string;
  description?: string | null;
  /** Access tag ids; reconciled by diff (tenant-scoped). */
  atag?: number[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' for system/global policies (no tenant). */
  tour_operator_id?: TenantIdsParam;
  /** Any of the 12 flags — only send `true` to filter to matching rows. */
  flags?: Partial<Record<LaunchPolicyFlagKey, boolean>>;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.flags) {
    for (const [k, v] of Object.entries(params.flags)) {
      if (v !== undefined) usp.set(k, String(v));
    }
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<LaunchPolicyListItem>> {
  return apiGet<PaginatedResponse<LaunchPolicyListItem>>(`/launch_policies/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<LaunchPolicyDetail> {
  return apiGet<LaunchPolicyDetail>(`/launch_policies/${id}/`);
}

export function create(body: LaunchPolicyWrite, reason?: string): Promise<LaunchPolicyDetail> {
  return apiPost<LaunchPolicyDetail>('/launch_policies/', body, withChangeReason(reason));
}

export function update(
  id: number,
  body: LaunchPolicyWrite,
  reason?: string,
): Promise<LaunchPolicyDetail> {
  return apiPatch<LaunchPolicyDetail>(`/launch_policies/${id}/`, body, withChangeReason(reason));
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/launch_policies/${id}/`);
}
